// ========================================
// Landing Page Scroll Effects
// ========================================

(function() {
  // Configuration
  const CONFIG = {
    fadeStart: 0.20,
    fadeEnd: 0.25,
    buttonFadeDelay: 0.2,
    buttonWidth: 170,
    gap: 80,
    desktopBreakpoint: 1024
  };

  // DOM Elements
  const hero = document.querySelector('.landing-hero');
  const actions = hero?.querySelector('.landing-actions');
  const heading = hero?.querySelector('#landing-hero-title');
  const header = document.querySelector('.landing-header');
  const isV2Hero = hero?.classList.contains('landing-hero--v2');
  const isV2Header = !!document.querySelector('.landing-header__inner-v2');

  // Portal actions to body to escape section stacking contexts (v1 desktop behavior only)
  (function portalActions() {
    if (!actions || isV2Hero) return;
    let portal = document.getElementById('landing-actions-portal');
    if (!portal) {
      portal = document.createElement('div');
      portal.id = 'landing-actions-portal';
      document.body.appendChild(portal);
    }
    if (actions.parentElement !== portal) {
      portal.appendChild(actions);
    }
  })();

  const buttonContainer = isV2Header
    ? document.querySelector('.landing-hero-button-container-v2')
    : document.querySelector('.landing-hero-button-container');
  const actionsGroup = document.querySelector('.landing-header__actions');
  const headerInner = isV2Header
    ? document.querySelector('.landing-header__inner-v2')
    : document.querySelector('.landing-header__inner');
  const nextSection = hero?.closest('.shopify-section')?.nextElementSibling;
  const mainContent = document.querySelector('.main-content');

  // Early exit if required elements don't exist
  if (!hero || !actions || !nextSection) return;

  // Utilities
  const clamp01 = n => Math.max(0, Math.min(1, n));
  const isDesktop = () => window.innerWidth >= CONFIG.desktopBreakpoint;

  // Store original header width for calculations
  let originalHeaderWidth = headerInner?.scrollWidth;

  // Handle hero actions fade
  function updateHeroFade(fade) {
    actions.style.setProperty('--fade', fade.toFixed(3));
    // Only disable pointer events when significantly faded (0.9 instead of 0.5)
    // This allows clicking even when slightly faded
    actions.style.pointerEvents = fade > 0.9 ? 'none' : 'auto';
  }

  // Keep actions 24px below heading while fixed (v1 only)
  function updateActionsTop() {
    if (!actions || !heading || isV2Hero) return;
    const r = heading.getBoundingClientRect();
    const desiredTop = Math.round(r.bottom + 24);
    // Optional clamp to keep within viewport
    const maxTop = window.innerHeight - 16;
    const minTop = (header?.offsetHeight || 0) + 8;
    const clampedTop = Math.max(minTop, Math.min(desiredTop, maxTop));
    actions.style.top = `${clampedTop}px`;
  }

  // Handle header button fade and expansion (v1 only — v2 buttons are always visible)
  function updateHeaderButton(fade) {
    if (!buttonContainer || !isDesktop() || isV2Header) return;

    // Rescale to reach 1.0 at end, honoring delay
    const d = CONFIG.buttonFadeDelay;
    const buttonFade = Math.max(0, Math.min(1, (fade - d) / (1 - d)));
    buttonContainer.style.setProperty('--button-fade', buttonFade.toFixed(3));

    // Animate width to avoid layout gap before reveal
    const btn = buttonContainer.firstElementChild;
    const naturalWidth = btn ? Math.ceil(btn.offsetWidth) : CONFIG.buttonWidth; // padding+border included
    const extra = 8; // small buffer to prevent clipping when container overflows hidden
    const targetWidth = Math.round((naturalWidth + extra) * buttonFade);
    buttonContainer.style.width = `${targetWidth}px`;

    // Drive inter-button gap with same eased factor (v1: 48px, v2: 14px)
    if (actionsGroup) {
      const gapMax = isV2Header ? 14 : 48;
      const gap = Math.round(gapMax * buttonFade);
      actionsGroup.style.columnGap = `${gap}px`;
    }
  }

  // Main scroll handler
  function handleScroll() {
    const vh = window.innerHeight;
    const nextRect = nextSection.getBoundingClientRect();
    const nextVisible = Math.max(0, vh - nextRect.top);
    const visibleRatio = nextVisible / Math.max(1, nextRect.height);

    const t = (visibleRatio - CONFIG.fadeStart) / (CONFIG.fadeEnd - CONFIG.fadeStart);
    const fade = clamp01(t);
    
    updateHeroFade(fade);
    updateHeaderButton(fade);
  }

  // Handle window resize
  function handleResize() {
    if (headerInner) {
      originalHeaderWidth = headerInner.scrollWidth;
    }
    updateActionsTop();
    handleScroll();
  }

  // Set up scroll listeners (use window on non-landing templates)
  function getScrollTarget() {
    if (!mainContent) return window;
    const styles = getComputedStyle(mainContent);
    const overflowY = styles.overflowY;
    const isScrollable =
      mainContent.scrollHeight > mainContent.clientHeight || /auto|scroll|overlay/.test(overflowY);
    return isScrollable ? mainContent : window;
  }

  const scrollTarget = getScrollTarget();
  scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleResize);
  
  // Initial call
  updateActionsTop();
  if (isV2Header) {
    buttonContainer?.style.removeProperty('width');
    buttonContainer?.style.removeProperty('--button-fade');
    actionsGroup?.style.removeProperty('column-gap');
  }
  handleScroll();
})();

// ========================================
// Scroll to Video Section
// ========================================

(function() {
  // Scroll to video section
  const scrollToVideoBtns = document.querySelectorAll('.js-scroll-to-video');
  const videoSection = document.querySelector('.hero-scroll-scrub');
  
  if (scrollToVideoBtns.length === 0 || !videoSection) return;

  scrollToVideoBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      
      // Smooth scroll to video section
      videoSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  });
})();

// ========================================
// Modal Functionality
// ========================================

(function() {
  const modal = document.getElementById('landing-modal');
  if (!modal) return;

  const body = document.body;

  // Open modal
  function openModal() {
    modal.setAttribute('aria-hidden', 'false');
    body.classList.add('modal-open');
  }

  // Close modal
  function closeModal() {
    modal.setAttribute('aria-hidden', 'true');
    body.classList.remove('modal-open');
  }

  // Event delegation for modal triggers
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-modal-trigger]');
    if (trigger) {
      e.preventDefault();
      openModal();
    }

    const closeButton = e.target.closest('[data-modal-close]');
    if (closeButton) {
      e.preventDefault();
      closeModal();
    }
  });

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
      closeModal();
    }
  });
})();

// ========================================
// Klaviyo Form Initialization
// ========================================

(function() {
  const modal = document.getElementById('landing-modal');
  if (!modal) return;

  const formContainer = modal.querySelector('.landing-modal__form > div');
  if (!formContainer) return;

  const formClass = formContainer.className;
  
  // Function to initialize Klaviyo form
  function initializeKlaviyoForm() {
    // Check if Klaviyo is loaded
    if (typeof window._klOnsite !== 'undefined') {
      // Force Klaviyo to render forms in this container
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['render', formClass]);
      
      // Check if form rendered after a delay
      setTimeout(() => {
        const formContent = formContainer.innerHTML;
        if (formContent.trim() === '') {
          // Try alternative rendering method
          window._klOnsite.push(['identify', function() {
            window._klOnsite.push(['render', formClass]);
          }]);
        }
      }, 1000);
    } else {
      // Retry after a short delay if Klaviyo isn't loaded yet
      setTimeout(initializeKlaviyoForm, 100);
    }
  }

  // Initialize form when modal opens
  function onModalOpen() {
    // Small delay to ensure modal is fully visible
    setTimeout(initializeKlaviyoForm, 100);
  }

  // Listen for modal open events
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
        if (modal.getAttribute('aria-hidden') === 'false') {
          onModalOpen();
        }
      }
    });
  });

  observer.observe(modal, { attributes: true });
})();

// ========================================
// Landing Header V2: Light Theme on Scroll
// (Always visible — no directional hide/show)
// ========================================

(function() {
  const headerInnerV2 = document.querySelector('.landing-header__inner-v2');
  if (!headerInnerV2) return;

  const header = document.querySelector('.landing-header');
  const videoSection = document.querySelector('.hero-scroll-scrub');
  if (!header || !videoSection) return;

  const mainContent = document.querySelector('.main-content');

  function getScrollTarget() {
    if (!mainContent) return window;
    const styles = getComputedStyle(mainContent);
    const overflowY = styles.overflowY;
    const isScrollable =
      mainContent.scrollHeight > mainContent.clientHeight || /auto|scroll|overlay/.test(overflowY);
    return isScrollable ? mainContent : window;
  }

  const scrollTarget = getScrollTarget();

  function isPastVideo() {
    const videoRect = videoSection.getBoundingClientRect();
    const videoBottom = videoRect.bottom;
    const headerHeight = header.offsetHeight || 80;
    return videoBottom <= headerHeight + 16;
  }

  function updateHeader() {
    header.classList.remove('landing-header--directional', 'landing-header--revealed');
    header.classList.toggle('landing-header--light', isPastVideo());
  }

  scrollTarget.addEventListener('scroll', updateHeader, { passive: true });
  window.addEventListener('resize', updateHeader, { passive: true });
  updateHeader();
})();